import { catalogSourceLabel } from "../../utils/regulatory"
import { ReloadOutlined } from "@ant-design/icons"
import { Alert, Button, Card, Table, Tabs } from "antd"
import { useCallback, useMemo } from "react"
import { Link, useLocation } from "react-router-dom"
import type { ModelPack, SourceDocument } from "../../types/regulatory"
import { allCatalogPages, regulatoryApi } from "../../utils/regulatoryApi"
import { normalizeString } from "../../utils/users"
import { CatalogFrame, JsonDetails, ResourceState, SearchInput } from "./components/CatalogUI"
import { textValue } from "./format"
import { useCatalogFilters, useCatalogResource } from "./useCatalog"
export function SourcesPage() {
  const location = useLocation()
  const tab = new URLSearchParams(location.search).get("tab") === "packs" ? "packs" : "documents"
  const { filters, page, update } = useCatalogFilters()
  const resource = useCatalogResource(
    useCallback(async (signal) => {
      const [documents, packs, references] = await Promise.all([
        allCatalogPages(regulatoryApi.documents, signal),
        allCatalogPages(regulatoryApi.packs, signal),
        allCatalogPages(regulatoryApi.references, signal),
      ])
      return { documents, packs, references }
    }, []),
  )
  const filtered = useMemo(() => {
    const search = normalizeString(filters.q ?? "")
    return {
      documents: (resource.data?.documents ?? []).filter((row) =>
        normalizeString(JSON.stringify(row)).includes(search),
      ),
      packs: (resource.data?.packs ?? []).filter((row) =>
        normalizeString(JSON.stringify(row) + " " + catalogSourceLabel(row.sourceNamespace)).includes(search),
      ),
    }
  }, [resource.data, filters.q])
  const pagination = {
    current: page,
    pageSize: filters.limit,
    onChange: (next: number, size: number) => update({ page: next, limit: size }),
  }
  return (
    <CatalogFrame
      title="Documents et paquets sources"
      extra={
        <Button icon={<ReloadOutlined />} onClick={resource.reload}>
          Actualiser
        </Button>
      }
    >
      <SearchInput
        value={filters.q ?? ""}
        onSearch={(value) => update({ q: value })}
        placeholder="Rechercher une source dans la liste complète"
      />
      <ResourceState {...resource} retry={resource.reload}>
        <Card>
          <Tabs
            activeKey={tab}
            onChange={(value) => update({ tab: value, q: undefined })}
            items={[
              {
                key: "documents",
                label: "Documents sources",
                children: (
                  <>
                    <Alert
                      type="info"
                      showIcon
                      message="Le catalogue contient les références des fichiers sources. Les PDF eux-mêmes ne sont pas disponibles ici."
                      style={{ marginBottom: 16 }}
                    />
                    <Table<SourceDocument>
                      rowKey="uuid"
                      dataSource={filtered.documents}
                      pagination={{ ...pagination, total: filtered.documents.length }}
                      columns={[
                        { title: "Fichier", dataIndex: "filename" },
                        {
                          title: "Édition de référence",
                          render: (_value, row) => (
                            <Link
                              to={`/regulatory/references?q=${encodeURIComponent(row.referenceEditionUuid)}`}
                            >
                              {resource.data?.references.find(
                                (reference) => reference.uuid === row.referenceEditionUuid,
                              )?.designation ?? "Consulter l’édition"}
                            </Link>
                          ),
                        },
                      ]}
                      expandable={{
                        expandedRowRender: (row) => <JsonDetails value={row.metadata} />,
                      }}
                    />
                  </>
                ),
              },
              {
                key: "packs",
                label: "Paquets de modèles",
                children: (
                  <Table<ModelPack>
                    rowKey="uuid"
                    dataSource={filtered.packs}
                    pagination={{ ...pagination, total: filtered.packs.length }}
                    columns={[
                      { title: "Paquet", dataIndex: "externalId" },
                      { title: "Espace source", dataIndex: "sourceNamespace", render: (namespace: string) => catalogSourceLabel(namespace) },
                      {
                        title: "Description",
                        render: (_value, row) => {
                          const source = row.metadata.source as Record<string, unknown> | undefined
                          return textValue(source?.title)
                        },
                      },
                      {
                        title: "Modèles",
                        render: (_value, row) => (
                          <Link
                            to={`/regulatory/templates?packUuid=${encodeURIComponent(row.uuid)}`}
                          >
                            Voir les modèles
                          </Link>
                        ),
                      },
                      {
                        title: "Points",
                        render: (_value, row) => (
                          <Link to={`/regulatory/points?packUuid=${encodeURIComponent(row.uuid)}`}>
                            Voir les points
                          </Link>
                        ),
                      },
                    ]}
                    expandable={{
                      expandedRowRender: (row) => <JsonDetails value={row.metadata} />,
                    }}
                  />
                ),
              },
            ]}
          />
        </Card>
      </ResourceState>
    </CatalogFrame>
  )
}
